import React, { useEffect, useState } from 'react'

/**
 * Match LandingPage / AnalysisDashboard: VITE_API_URL is the API origin only (no /api/v1).
 * Dev with empty env uses relative /api/v1/... (Vite proxy).
 */
function landlordReportUrl(searchParams) {
  const origin = import.meta.env.VITE_API_URL || ''
  const qs = searchParams.toString()
  const path = `/api/v1/landlord-report?${qs}`
  if (!origin) return path
  return `${String(origin).replace(/\/$/, '')}${path}`
}

/** FastAPI often returns `detail` as a string, object, or validation error array. */
function formatFastApiDetail(detail) {
  if (detail == null) return ''
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    return detail
      .map((x) => {
        if (x && typeof x === 'object' && x.msg != null) return String(x.msg)
        try {
          return JSON.stringify(x)
        } catch {
          return String(x)
        }
      })
      .filter(Boolean)
      .join('; ')
  }
  if (typeof detail === 'object') {
    if (detail.msg != null) return String(detail.msg)
    if (detail.message != null) return String(detail.message)
  }
  return String(detail)
}

async function errorMessageFromResponse(res) {
  const text = await res.text()
  if (!text) return `Request failed (${res.status})`
  try {
    const body = JSON.parse(text)
    const d = formatFastApiDetail(body?.detail)
    return d || text || `Request failed (${res.status})`
  } catch {
    return text
  }
}

/**
 * Austin Chambers white-labelled landlord report page.
 * POSTs the analysis result directly to the backend — no cache lookup needed.
 */
export default function LandlordReport({ analysisResult, premium = false, onClose }) {
  const [html, setHtml] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [pdfLoading, setPdfLoading] = useState(false)

  const pdfBlocked = analysisResult?.pdf_export_blocked === true

  useEffect(() => {
    if (!analysisResult) return
    setLoading(true)
    setError(null)

    const params = new URLSearchParams({ format: 'html' })
    if (premium) params.set('premium', 'true')

    fetch(landlordReportUrl(params), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(analysisResult),
    })
      .then(async (r) => {
        if (!r.ok) {
          const msg = await errorMessageFromResponse(r)
          throw new Error(msg)
        }
        return r.text()
      })
      .then((text) => {
        setHtml(text)
        setLoading(false)
      })
      .catch((err) => {
        setError(err.message)
        setLoading(false)
      })
  }, [analysisResult, premium])

  const handleDownloadPdf = async () => {
    if (!analysisResult || pdfBlocked) return
    setPdfLoading(true)
    try {
      const params = new URLSearchParams({ format: 'pdf' })
      if (premium) params.set('premium', 'true')
      const res = await fetch(landlordReportUrl(params), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(analysisResult),
      })
      if (!res.ok) {
        const msg = await errorMessageFromResponse(res)
        throw new Error(msg || `PDF generation failed (${res.status})`)
      }
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `AC_Report_${analysisResult.id || 'report'}.pdf`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      alert(`PDF download failed: ${err.message}`)
    } finally {
      setPdfLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div>
            <p className="text-sm font-semibold text-gray-800">Austin Chambers &amp; Co</p>
            <p className="text-xs text-gray-500">Landlord Investment Outlook</p>
          </div>
          {premium && (
            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">
              NRL Premium
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleDownloadPdf}
            disabled={pdfLoading || loading || !!error || pdfBlocked}
            title={
              pdfBlocked
                ? 'PDF export is blocked for this analysis until data-quality issues are resolved.'
                : undefined
            }
            className="flex items-center gap-1.5 text-sm px-4 py-1.5 bg-gray-900 text-white rounded-lg hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {pdfLoading ? (
              <>
                <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                Generating PDF…
              </>
            ) : (
              <>
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 4v11" />
                </svg>
                Download PDF
              </>
            )}
          </button>

          <button
            type="button"
            onClick={onClose}
            className="text-sm px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>

      {pdfBlocked && !loading && !error && (
        <div className="px-6 py-2 text-xs bg-amber-50 border-b border-amber-100 text-amber-900" role="status">
          PDF download is disabled for this run because the analysis is flagged for review or failed
          data-quality gates. The HTML report below is for context only.
        </div>
      )}

      {/* Report body */}
      <div className="flex-1 overflow-auto bg-gray-100">
        {loading && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <svg className="animate-spin h-8 w-8 text-gray-400 mx-auto mb-3" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              <p className="text-sm text-gray-500">Generating report…</p>
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center max-w-md px-4">
              <p className="text-sm font-semibold text-red-600 mb-1">Report unavailable</p>
              <p className="text-xs text-gray-500 whitespace-pre-wrap break-words">{error}</p>
            </div>
          </div>
        )}

        {html && !loading && (
          <iframe
            title="Landlord Report"
            srcDoc={html}
            className="w-full h-full border-0"
            sandbox="allow-same-origin"
          />
        )}
      </div>
    </div>
  )
}
